import { useMemo, useState } from 'react';
import {
  Pencil,
  RotateCcw,
  Save,
  Plus,
  ChevronUp,
  ChevronDown,
  EyeOff,
  LayoutDashboard,
} from 'lucide-react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useAuthStore } from '@/stores/auth-store';
import { SITE_TYPE, SITE_SHORT_NAME } from '@/config/site';
import {
  getAvailableWidgets,
  resolveWidgets,
  layoutFromLists,
  type DashboardWidgetDef,
} from '@/config/admin-dashboard';
import {
  useSiteDashboardLayout,
  readLocalLayout,
  writeLocalLayout,
} from '@/hooks/dashboard/useDashboardLayout';
import Skeleton from '@/components/ui/Skeleton';
import EmailDomainAlert from '@/components/admin/EmailDomainAlert';
import { cn } from '@/lib/utils';

const spanClass: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'md:col-span-2 lg:col-span-2',
  3: 'md:col-span-2 lg:col-span-3',
  4: 'md:col-span-2 lg:col-span-4',
};

type Lists = { visible: DashboardWidgetDef[]; hidden: DashboardWidgetDef[] };

export default function DashboardHome() {
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const { user } = useAuthStore();
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;
  const { siteLayout, isLoading: siteLoading, save } = useSiteDashboardLayout();

  const [localLayout, setLocalLayout] = useState(() => readLocalLayout());
  const [draft, setDraft] = useState<Lists | null>(null);
  const editing = draft !== null;

  const available = useMemo(
    () => getAvailableWidgets(isEnabled, SITE_TYPE, isPlatformAdmin),
    [isEnabled, isPlatformAdmin],
  );

  const saved = localLayout ?? siteLayout ?? null;
  const resolved = useMemo(() => resolveWidgets(available, saved), [available, saved]);

  // Wait for feature flags (isEnabled fails open while loading → would flash
  // every widget). Also wait for the site layout unless the admin already has
  // a local override to render from.
  const ready = !flagsLoading && (localLayout !== null || !siteLoading);

  const lists: Lists = editing ? (draft as Lists) : resolved;

  // ── Edit actions ──────────────────────────────────────────────────────────
  const enterEdit = () =>
    setDraft({ visible: [...resolved.visible], hidden: [...resolved.hidden] });
  const cancelEdit = () => setDraft(null);

  const move = (idx: number, dir: -1 | 1) =>
    setDraft((d) => {
      if (!d) return d;
      const v = [...d.visible];
      const j = idx + dir;
      if (j < 0 || j >= v.length) return d;
      [v[idx], v[j]] = [v[j], v[idx]];
      return { ...d, visible: v };
    });

  const hide = (key: string) =>
    setDraft((d) => {
      if (!d) return d;
      const w = d.visible.find((x) => x.key === key);
      if (!w) return d;
      return {
        visible: d.visible.filter((x) => x.key !== key),
        hidden: [...d.hidden, w],
      };
    });

  const show = (key: string) =>
    setDraft((d) => {
      if (!d) return d;
      const w = d.hidden.find((x) => x.key === key);
      if (!w) return d;
      return {
        hidden: d.hidden.filter((x) => x.key !== key),
        visible: [...d.visible, w],
      };
    });

  const resetToDefault = () => {
    const def = resolveWidgets(available, null);
    setDraft({ visible: [...def.visible], hidden: [...def.hidden] });
  };

  const persistLocal = (d: Lists) => {
    const layout = layoutFromLists(d.visible, d.hidden);
    writeLocalLayout(layout);
    setLocalLayout(layout);
  };

  const saveMine = () => {
    if (draft) persistLocal(draft);
    setDraft(null);
  };

  const saveSiteDefault = () => {
    if (!draft) return;
    const layout = layoutFromLists(draft.visible, draft.hidden);
    save.mutate(layout);
    persistLocal(draft);
    setDraft(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Dashboard</h1>
          <p className="mt-1 text-sm text-stone-500">
            Welcome back to {SITE_SHORT_NAME}. Here's what's happening today.
          </p>
        </div>

        {ready && (
          <div className="flex items-center gap-2">
            {!editing ? (
              <button
                onClick={enterEdit}
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-stone-600 hover:bg-stone-50"
              >
                <Pencil className="h-4 w-4" />
                Customize
              </button>
            ) : (
              <>
                <button
                  onClick={resetToDefault}
                  className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-stone-500 hover:text-stone-700"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset
                </button>
                <button
                  onClick={cancelEdit}
                  className="rounded-md px-3 py-1.5 text-sm font-medium text-stone-500 hover:text-stone-700"
                >
                  Cancel
                </button>
                <button
                  onClick={saveSiteDefault}
                  disabled={save.isPending}
                  title="Make this the default layout for everyone on this site"
                  className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-stone-600 hover:bg-stone-50 disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {save.isPending ? 'Saving…' : 'Save as site default'}
                </button>
                <button
                  onClick={saveMine}
                  className="rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700"
                >
                  Done
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Action-needed notice when the tenant's email domain isn't verified. */}
      <EmailDomainAlert />

      {editing && (
        <p className="rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-800">
          Reorder with the arrows, hide widgets you don't need, and add more from
          the panel below. <strong>Done</strong> saves your personal view;{' '}
          <strong>Save as site default</strong> applies to every admin on this site.
        </p>
      )}

      {!ready ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="md:col-span-2">
              <Skeleton className="h-40 rounded-xl" />
            </div>
          ))}
        </div>
      ) : lists.visible.length === 0 && !editing ? (
        <div className="rounded-xl border border-dashed border-line p-10 text-center">
          <LayoutDashboard className="mx-auto mb-3 h-10 w-10 text-stone-300" />
          <p className="text-sm text-stone-500">
            Your dashboard is empty. Click <strong>Customize</strong> to add widgets.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {lists.visible.map((w, idx) => {
            const Widget = w.Component;
            return (
              <div key={w.key} className={cn(spanClass[w.span ?? 1])}>
                {editing && (
                  <div className="mb-1 flex items-center gap-1 rounded-md bg-stone-100 px-2 py-1">
                    <span className="mr-auto truncate text-xs font-medium text-stone-500">
                      {w.title}
                    </span>
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="rounded p-1 text-stone-500 hover:bg-stone-200 disabled:opacity-30"
                      aria-label={`Move ${w.title} up`}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === lists.visible.length - 1}
                      className="rounded p-1 text-stone-500 hover:bg-stone-200 disabled:opacity-30"
                      aria-label={`Move ${w.title} down`}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => hide(w.key)}
                      className="rounded p-1 text-stone-500 hover:bg-stone-200"
                      aria-label={`Hide ${w.title}`}
                    >
                      <EyeOff className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <div className={cn(editing && 'pointer-events-none opacity-60')}>
                  <Widget />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="rounded-xl border border-dashed border-line p-4">
          <h3 className="mb-2 text-sm font-semibold text-ink">Add a widget</h3>
          {lists.hidden.length === 0 ? (
            <p className="text-xs text-stone-500">
              All available widgets are on your dashboard.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {lists.hidden.map((w) => {
                const Icon = w.icon;
                return (
                  <button
                    key={w.key}
                    onClick={() => show(w.key)}
                    title={w.description}
                    className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface px-3 py-1.5 text-sm font-medium text-stone-600 hover:border-green-200 hover:bg-green-50 hover:text-green-800"
                  >
                    <Icon className="h-4 w-4" />
                    {w.title}
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
